import { Input, Wrap, Button, VStack, Text, Checkbox, FormLabel, HStack, Select } from "@chakra-ui/react";
import Link from "next/link";
import { useState } from "react";

import type { FormStepProps } from "../interfaces";
import FormInputWraper from "../Components/FormInputWrapper";
import RegisterHeading from "../Components/RegisterHeading";
import { FormWrapper } from "../Components/LayoutComponents";
import { INPUT_SIZE, INPUT_SPACING, INPUT_VARIANT } from "../RegisterPage";
import { getPaths } from "lib/utils/paths";
import { useTranslations } from "next-intl";
import { Controller } from "react-hook-form";
import { useLocales } from "src/contexts/LocalesContext";

const StepOne = ({
  onSubmit,
  FormController,
  RegisterMutationResult,
}: FormStepProps) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { locales } = useLocales();
  const t = useTranslations("pages.register");
  return (
    <FormWrapper>
      <RegisterHeading
        color="brandOrange.500"
        fontSize="4xl"
        mb="8"
      >
        {t("personaData")}
      </RegisterHeading>
      <VStack spacing={INPUT_SPACING} w="full">
        <FormInputWraper
          FormController={FormController}
          field_name="name"
          color="brandOrange.500"
          label={t("name")}
          isRequired
        >
          <Input
            key="name"
            variant={INPUT_VARIANT}
            size={INPUT_SIZE}
            autoComplete="name"
            id="name"
            {...FormController.register("name", {
              required: t("fullNameMessage"),
            })}
          />
        </FormInputWraper>
        <Wrap dir="row" w="full" spacing={INPUT_SPACING}>
          <FormInputWraper
            flex={1}
            minW={["full", "full", "full", "40%"]}
            FormController={FormController}
            field_name="email"
            color="brandOrange.500"
            label={t("email")}
            isRequired
          >
            <Input
              key="email"
              variant={INPUT_VARIANT}
              size={INPUT_SIZE}
              id="email"
              autoComplete="email"
              {...FormController.register("email", {
                required: t("emailMessage"),
                pattern: {
                  value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  message: t("validEmail"),
                },
              })}
            />
          </FormInputWraper>
          <FormInputWraper
            flex={1}
            minW={["full", "full", "full", "40%"]}
            FormController={FormController}
            field_name="phone"
            color="brandOrange.500"
            label={t("cellphone")}
          >
            <Input
              key="phone"
              variant={INPUT_VARIANT}
              size={INPUT_SIZE}
              id="phone"
              autoComplete="tel"
              {...FormController.register("phone")}
            />
          </FormInputWraper>
        </Wrap >

        <Wrap dir="row" w="full" spacing={INPUT_SPACING}>
          <FormInputWraper
            isRequired
            flex={1}
            minW={["full", "full", "full", "40%"]}
            FormController={FormController}
            field_name="password"
            color="brandOrange.500"
            label={t("password")}
          >
            <Input
              key="password"
              variant={INPUT_VARIANT}
              size={INPUT_SIZE}
              type="password"
              {...FormController.register("password", {
                required: t("requiredField"),
              })}
            />
          </FormInputWraper>
          <FormInputWraper
            flex={1}
            minW={["full", "full", "full", "40%"]}
            FormController={FormController}
            field_name="confirm_password"
            color="brandOrange.500"
            label={t("matchPassword")}
            isRequired
          >
            <Input
              key="confirm_password"
              variant={INPUT_VARIANT}
              size={INPUT_SIZE}
              type="password"
              {...FormController.register("confirm_password", {
                required: t("requiredField"),
                validate: value =>
                  value === FormController.getValues("password") ||
                  t("noMatchPassword"),
              })}
            />
          </FormInputWraper >
        </Wrap >
        <Wrap dir="row" w="full" spacing={INPUT_SPACING}>
          <FormInputWraper
            label={t("emailLanguage")}
            FormController={FormController}
            minW={["full", "full", "full", "40%"]}
            field_name="email_language"
            color="brandOrange.500"
            key="email_language"
            isRequired
          >
            <Controller
              name="email_language"
              control={FormController.control}
              rules={{ required: t("requiredSelectionLanguage") }}
              render={({ field: { onChange } }) => (
                <Select onChange={({ target: { value } }) => onChange(value)}>
                  {locales.map(loc => (
                    <option
                      key={loc.id}
                      value={loc.code}
                      selected={loc.code === FormController.getValues("email_language")}
                    >
                      {loc.language}
                    </option>
                  ))}
                </Select>
              )}
            />
          </FormInputWraper>
        </Wrap>
        <HStack w="full" align="start">
          <Checkbox
            id="terms"
            key="terms"
            colorScheme="brandOrange"
            variant="outline"
            size="lg"
            isChecked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
          />
          <FormLabel
            htmlFor="terms"
            color="textGray"
            fontSize="xs"
          >
            {t("termsAndConditions1")}{" "}
            <a
              href={getPaths.termsOfService()}
              target="_blank" // this does not work with Next Link yet (>v13 is required)
              rel="noreferrer"
            >
              {t("termsAndConditions2")}
            </a>
            {" "}{t("termsAndConditions3")}
          </FormLabel>
        </HStack>
        {
          RegisterMutationResult?.error && (
            <VStack>
              <Text>{RegisterMutationResult?.error.message}</Text>
              {RegisterMutationResult?.error.message?.includes("registado") && (
                <Link href={getPaths.login()}>{t("signInMessage")}</Link>
              )}
            </VStack>
          )
        }
      </VStack >
      <Button
        type="submit"
        colorScheme="brandOrange"
        onClick={FormController.handleSubmit(onSubmit)}
        isDisabled={!termsAccepted || !!Object.keys(FormController.formState.errors).length}
      >
        {t("nextAthleteButton1")}
      </Button>
    </FormWrapper >
  );
};

export default StepOne;
